import React, { useCallback, useMemo } from 'react';
import { HStack, VStack } from '@cbhq/cds-web/layout';
import { Text } from '@cbhq/cds-web/typography';
import { useBreakpoints } from '@cbhq/cds-web/hooks/useBreakpoints';
import { Button } from '@cbhq/cds-web/buttons/Button';
import { useCompliance } from '@/components/ComplianceProvider/ComplianceProvider';
import { useAddLiquidityModal } from '@/components/AddLiquidity/AddLiquidityModalProvider';

function PortfolioHeaderButtons() {
  const { isPhone, isPhoneLandscape } = useBreakpoints();
  const showMobileLayout = useMemo(() => isPhone ?? isPhoneLandscape, [isPhone, isPhoneLandscape]);

  const { isCompliant, openComplianceModals } = useCompliance();
  const { openAddLiquidityModal } = useAddLiquidityModal();

  const handleCreatePosition = useCallback(() => {
    if (!isCompliant) {
      openComplianceModals();
    } else {
      openAddLiquidityModal();
    }
  }, [isCompliant, openComplianceModals, openAddLiquidityModal]);

  const buttonPositionStyle = useMemo(() => {
    if (showMobileLayout) {
      return {
        style: {
          position: 'fixed' as const,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 39, //ock wallet overlay on mobile has z-index 40
        },
      };
    }
    return {};
  }, [showMobileLayout]);

  const CustomButton = useCallback(
    ({
      onPress,
      isCollectFees,
      testID,
    }: {
      isCollectFees?: boolean;
      onPress: () => void;
      testID?: string;
    }) => {
      return (
        <Button
          as="button"
          block={showMobileLayout}
          variant={isCollectFees ? 'secondary' : 'primary'}
          onClick={onPress}
          compact={!showMobileLayout}
          testID={testID}
        >
          <Text font="label1" as="p" color={isCollectFees ? 'currentColor' : 'fgInverse'}>
            {isCollectFees ? 'Collect fees' : 'Create position'}
          </Text>
        </Button>
      );
    },
    [showMobileLayout],
  );
  const Container = useMemo(() => (showMobileLayout ? VStack : HStack), [showMobileLayout]);
  return (
    <Container
      gap={1}
      padding={showMobileLayout ? 3 : 0}
      paddingBottom={showMobileLayout ? 4 : 0}
      background={showMobileLayout ? 'bg' : 'transparent'}
      elevation={showMobileLayout ? 1 : 0}
      {...buttonPositionStyle}
    >
      <CustomButton
        isCollectFees={false}
        onPress={handleCreatePosition}
        testID="portfolio-create-position-button"
      />
    </Container>
  );
}

export { PortfolioHeaderButtons };
